import React, { memo, useCallback, useEffect } from 'react';
import { FieldErrors } from 'react-hook-form';
import { ValidationError } from 'yup';

import { Button, Input, RadioButton, TextArea } from '@components/common';
import { ETabsIds } from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/tabsIds';

import { filterValuesByKeys } from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/utils';

import { IAddKpiAlertBasicInformationProps, IBasicInformationFields } from '../interfaces';

import { EKpiAlertsStatus, ESeverity } from '@enums/kpi-common';
import { EKpiBasicInformationFields } from './enums';

import { ERadioColors } from '@components/common/RadioButton/constants';

import { basicInformationFields, basicInformationSchema } from './schema';

import styles from './AddKpiAlertBasicInformation.module.scss';

function AddKpiAlertBasicInformation({
	setActiveTab,
	formMethods,
	formState,
	values,
	positiveBtnText,
	negativeBtnText,
	isEdit,
	negativeHandler,
	positiveHandler,
}: IAddKpiAlertBasicInformationProps) {
	const {
		watch,
		register,
		setError,
		setValue,
		handleSubmit,
	} = formMethods;

	const { errors }: { errors: FieldErrors } = formState;

	const status = watch(EKpiBasicInformationFields.status);
	const severity = watch(EKpiBasicInformationFields.severity);

	const filteredValuesByKeys: IBasicInformationFields = filterValuesByKeys(
		values,
		Object.keys(basicInformationFields),
	);

	const onHandleNext = useCallback(async (): Promise<void> => {
			if (isEdit) {
				return handleSubmit(positiveHandler)();
			}

			try {
				await basicInformationSchema.validate(filteredValuesByKeys, { abortEarly: false });

				setActiveTab(ETabsIds.checkFor);
			} catch (e) {
				const { message, path, inner } = e as ValidationError;

				if (path) {
					setError(path, { message: message as string });
				} else {
					inner.forEach((item: any) => {
						setError(item.path, { message: item.message });
					});
				}
			}
		},
		[
			filteredValuesByKeys,
			positiveHandler,
			handleSubmit,
			isEdit,
			setActiveTab,
			setError,
		]);

	const setFieldValue = useCallback((name: string) => (value: string): void => {
		setValue(name, value, { shouldValidate: true, shouldDirty: true });
	}, [setValue]);

	const isSubmitBtnDisabled: boolean = Object.keys(filteredValuesByKeys).some((key) => !!errors[key]);

	useEffect(() => {
		const handleEnterKeyPress = (e: KeyboardEvent) => {
			if (e.key === 'Enter' && !isSubmitBtnDisabled) {
				void onHandleNext();
			}
		};

		document.addEventListener('keydown', handleEnterKeyPress);

		return () => {
			document.removeEventListener('keydown', handleEnterKeyPress);
		};
	}, [isSubmitBtnDisabled, onHandleNext]);

	return (
		<div className={styles.basicInfoContainer}>
			<div className='fieldLabel'>
				<span>Status</span>
				<span className='asterisk'>*</span>
			</div>
			<div className='fieldsContainer'>
				<RadioButton
					checked={status === EKpiAlertsStatus.active}
					id={EKpiAlertsStatus.active}
					label='Active'
					name={EKpiBasicInformationFields.status}
					onChange={setFieldValue(EKpiBasicInformationFields.status)}
					register={register}
					value={EKpiAlertsStatus.active}
					withUseForms
				/>
				<RadioButton
					checked={status === EKpiAlertsStatus.inactive}
					id={EKpiAlertsStatus.inactive}
					label='Inactive'
					name={EKpiBasicInformationFields.status}
					onChange={setFieldValue(EKpiBasicInformationFields.status)}
					register={register}
					value={EKpiAlertsStatus.inactive}
					withUseForms
				/>
			</div>
			<div className='fieldLabel'>
				<span>Severity</span>
				<span className='asterisk'>*</span>
			</div>
			<div className='fieldsContainer flex-column'>
				<RadioButton
					checked={severity === ESeverity.critical}
					color={ERadioColors.red}
					id={ESeverity.critical}
					label='Critical'
					name={EKpiBasicInformationFields.severity}
					onChange={setFieldValue(EKpiBasicInformationFields.severity)}
					register={register}
					value={ESeverity.critical}
					withUseForms
				/>
				<RadioButton
					checked={severity === ESeverity.error}
					color={ERadioColors.orange}
					id={ESeverity.error}
					label='Error'
					name={EKpiBasicInformationFields.severity}
					onChange={setFieldValue(EKpiBasicInformationFields.severity)}
					register={register}
					value={ESeverity.error}
					withUseForms
				/>
				<RadioButton
					checked={severity === ESeverity.warning}
					color={ERadioColors.amber}
					id={ESeverity.warning}
					label='Warning'
					name={EKpiBasicInformationFields.severity}
					onChange={setFieldValue(EKpiBasicInformationFields.severity)}
					register={register}
					value={ESeverity.warning}
					withUseForms
				/>
				<RadioButton
					checked={severity === ESeverity.informational}
					color={ERadioColors.green}
					id={ESeverity.informational}
					label='Informational'
					name={EKpiBasicInformationFields.severity}
					onChange={setFieldValue(EKpiBasicInformationFields.severity)}
					register={register}
					value={ESeverity.informational}
					withUseForms
				/>
			</div>
			<div className='fieldsContainer'>
				<Input
					className={styles.ruleName}
					errorMessage={errors?.ruleName?.message}
					isError={!!errors?.ruleName?.message}
					label='Rule name'
					name={EKpiBasicInformationFields.ruleName}
					onChange={setFieldValue(EKpiBasicInformationFields.ruleName)}
					placeholder='Enter rule name'
					register={register}
					required
					value={values[EKpiBasicInformationFields.ruleName]}
				/>
			</div>
			<div className='fieldsContainer mb-50'>
				<TextArea
					errorMessage={errors?.ruleDescription?.message}
					label='Rule description'
					name={EKpiBasicInformationFields.ruleDescription}
					onChange={setFieldValue(EKpiBasicInformationFields.ruleDescription)}
					placeholder='Enter description'
					register={register}
					rows={3}
					withUseForms
				/>
			</div>
			<div className='buttonsContainer'>
				<Button
					disabled={!isEdit}
					onClick={negativeHandler}
					text={negativeBtnText}
					withoutBackground
				/>
				<Button
					disabled={isSubmitBtnDisabled}
					onClick={onHandleNext}
					text={positiveBtnText}
				/>
			</div>
		</div>
	);
}

export default memo(AddKpiAlertBasicInformation);
